import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import UnifiedDashboard from '../../../components/layout/UnifiedDashboard';
import ProductCard from '../../../components/product/ProductCard';
import { isAuthenticated } from '../../../services/auth';
import { userActiveProducts, userPendingProducts, userClosedProducts } from '../../../services/user';
import { closeProduct, updateProductStatus, allProducts } from '../../../services/product';
import { productStatus, getUsers } from '../../../services/admin';
import { Empty, Pagination, message, Badge, Tabs, Select, Input } from 'antd';
import './UserDashboard.css';

const UserDashboard = () => {
  const { user } = isAuthenticated();
  const [current, setCurrent] = useState(1);
  const [filter, setFilter] = useState('active');
  const [statuses, setStatuses] = useState(['active', 'pending', 'closed']);
  const [statusChangeLoading, setStatusChangeLoading] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [activeProducts, setActiveProducts] = useState([]);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [closedProducts, setClosedProducts] = useState([]);
  
  const [isAdmin] = useState(user ? user.role === 'admin' : false);
  const countPerPage = isAdmin ? 20 : 5;
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const isLoadingRef = useRef(false);

  // Carrega todos os 3 status de uma vez
  const loadAllThreeStatuses = async () => {
    if (isLoadingRef.current || !user) return;
    isLoadingRef.current = true;
    setLoading(true);
    
    try {
      if (isAdmin) {
        const res = await allProducts();
        const allData = res.data;
        setActiveProducts(allData.filter(p => p.status === 'active'));
        setPendingProducts(allData.filter(p => p.status === 'pending'));
        setClosedProducts(allData.filter(p => p.status === 'closed'));
      } else {
        const [activeRes, pendingRes, closedRes] = await Promise.all([
          userActiveProducts(user._id),
          userPendingProducts(user._id),
          userClosedProducts(user._id)
        ]);
        setActiveProducts(activeRes.data);
        setPendingProducts(pendingRes.data);
        setClosedProducts(closedRes.data);
      }
    } catch (err) {
      console.error(err);
      message.error('Erro ao carregar produtos');
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  };

  // Carrega apenas o status específico
  const loadSingleStatus = async (status) => {
    if (isLoadingRef.current || !user) return;
    isLoadingRef.current = true;
    setLoading(true);
    
    try {
      if (isAdmin) {
        // Admin sempre carrega tudo para manter sincronizado
        const res = await allProducts();
        const allData = res.data;
        setActiveProducts(allData.filter(p => p.status === 'active'));
        setPendingProducts(allData.filter(p => p.status === 'pending'));
        setClosedProducts(allData.filter(p => p.status === 'closed'));
      } else {
        // Usuário comum carrega só o status atual
        if (status === 'active') {
          const res = await userActiveProducts(user._id);
          setActiveProducts(res.data);
        } else if (status === 'pending') {
          const res = await userPendingProducts(user._id);
          setPendingProducts(res.data);
        } else if (status === 'closed') {
          const res = await userClosedProducts(user._id);
          setClosedProducts(res.data);
        }
      }
    } catch (err) {
      console.error(err);
      message.error('Erro ao carregar produtos');
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  };

  // Obtém os produtos do status atual
  const getCurrentProducts = () => {
    let products;
    if (filter === 'active') products = activeProducts;
    else if (filter === 'pending') products = pendingProducts;
    else if (filter === 'closed') products = closedProducts;
    else products = [];

    // Filtra por usuário se admin e usuário selecionado
    if (isAdmin && selectedUserId) {
      products = products.filter(p => p.author && p.author._id === selectedUserId);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      
      let priceStr = '';
      if (/^[0-9.,R$\s]+$/.test(term)) {
        const cleanTerm = term.replace(/[^\d,]/g, '').replace(',', '.');
        const parsedPrice = parseFloat(cleanTerm);
        if (!isNaN(parsedPrice)) {
          priceStr = parsedPrice.toString();
        }
      } else {
        priceStr = term.replace(/\D/g, '');
      }

      products = products.filter(p => 
        (p.name && p.name.toLowerCase().includes(term)) ||
        (p.description && p.description.toLowerCase().includes(term)) ||
        (p.category && p.category.name && p.category.name.toLowerCase().includes(term)) ||
        (p.productLocation && p.productLocation.name && p.productLocation.name.toLowerCase().includes(term)) ||
        (p.price && priceStr && p.price.toString().includes(priceStr))
      );
    }

    return products;
  };

  // Obtém produtos paginados
  const getPaginatedProducts = () => {
    const products = getCurrentProducts();
    const to = current * countPerPage;
    const from = to - countPerPage;
    return products.slice(from, to);
  };

  // Carregamento inicial - apenas uma vez quando monta
  useEffect(() => {
    const init = async () => {
      // Carrega status disponíveis
      try {
        const res = await productStatus();
        setStatuses(res.data);
      } catch (err) {
        setStatuses(['active', 'pending', 'closed']);
      }

      // Carrega usuários se for admin
      if (isAdmin) {
        try {
          const res = await getUsers();
          setAllUsers(res.data);
        } catch (err) {
          console.error(err);
        }
      }

      // Carrega todos os 3 status
      await loadAllThreeStatuses();
    };

    if (user) {
      init();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = async (productId) => {
    try {
      await closeProduct(productId);
      message.success('Produto marcado como vendido!');
      // Recarrega todos os 3 status
      await loadAllThreeStatuses();
      setCurrent(1);
    } catch (err) {
      message.error('Erro ao marcar produto como vendido');
    }
  };

  const handleStatusChange = async (e, productId) => {
    const newStatus = e.target.value;
    setStatusChangeLoading(productId);
    try {
      await updateProductStatus({
        productId,
        status: newStatus,
        token: isAuthenticated().token,
      });
      message.success('Status atualizado com sucesso');
      
      // Recarrega todos os 3 status para atualização em tempo real
      await loadAllThreeStatuses();
      setCurrent(1);
    } catch (err) {
      message.error('Erro ao atualizar status');
    } finally {
      setStatusChangeLoading(null);
    }
  };

  // Handler para mudança de filtro (tab)
  const handleFilterChange = (key) => {
    setFilter(key);
    setCurrent(1);
    loadSingleStatus(key);
  };

  // Handler para mudança de usuário selecionado
  const handleUserChange = (val) => {
    setSelectedUserId(val);
    setCurrent(1);
  };

  // Contagem de produtos por status
  const getProductCount = (status) => {
    let products;
    if (status === 'active') products = activeProducts;
    else if (status === 'pending') products = pendingProducts;
    else if (status === 'closed') products = closedProducts;
    else return 0;

    if (isAdmin && selectedUserId) {
      products = products.filter(p => p.author && p.author._id === selectedUserId);
    }

    return products.length;
  };

  const activeCount = getProductCount('active');
  const pendingCount = getProductCount('pending');
  const closedCount = getProductCount('closed');

  const currentProducts = getCurrentProducts();
  const paginatedProducts = getPaginatedProducts();

  Number.prototype.format = function (n, x) {
    var re = '\\d(?=(\\d{' + (x || 3) + '})+' + (n > 0 ? '\\.' : '$') + ')';
    return this.toFixed(Math.max(0, ~~n)).replace(new RegExp(re, 'g'), '$&,');
  };

  const renderProductCard = (product, index) => (
    <div className='dashboard-container' key={product._id}>
      <ProductCard
        product={product}
        onStatusChange={handleStatusChange}
        onClose={handleClose}
        showActions={true}
        showAuthor={false}
        isAdmin={isAdmin}
        statuses={statuses}
        statusChangeLoading={statusChangeLoading}
        showStatus={true}
        editLink={`/edit-product/${product._id}`}
      />
    </div>
  );

  if (!user) {
    return <div>Por favor, faça login para acessar o dashboard.</div>;
  }

  return (
    <UnifiedDashboard>
      <div className='dashboard-content'>
        {/* Header Section */}
        <div>
          <div>
            <h2>{isAdmin ? 'Todos os Produtos' : 'Meus Produtos'}</h2>
            <p>{isAdmin ? 'Gerencie todos os anúncios do sistema' : 'Gerencie seus anúncios e controle seu inventário'}</p>
          </div>
          {!isAdmin && (
            <Link 
              to={`/add-product/${user._id}`}
              className='btn btn-primary btn-lg rounded-3 px-4'
            >
              <i className='fas fa-plus me-2'></i>
              Novo Produto
            </Link>
          )}
        </div>
        <div style={{ marginBottom: '30px', display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center' }}>
          <div style={{ flex: '1 1 300px' }}>
            <Input
              placeholder="Buscar por nome, descrição, categoria ou valor..."
              value={searchTerm}
              onChange={(e) => {
                let val = e.target.value;
                if (/[a-zA-Z]/.test(val) && /^[0-9.,R$\s]+$/.test(searchTerm)) {
                  val = val.replace(/[\d.,R$\s]/g, '').trim();
                } else if (val && /^[0-9.,R$\s]+$/.test(val) && /\d/.test(val)) {
                  const onlyNumbers = val.replace(/\D/g, '');
                  if (onlyNumbers && parseInt(onlyNumbers, 10) > 0) {
                    const numValue = parseInt(onlyNumbers, 10) / 100;
                    val = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numValue);
                  } else {
                    val = '';
                  }
                }
                setSearchTerm(val);
                setCurrent(1);
              }}
              prefix={<i className="fas fa-search" style={{ color: 'var(--text-secondary)' }}></i>}
              style={{ width: '100%', borderRadius: '4px', padding: '8px 12px' }}
              allowClear
            />
          </div>

          {/* Filtro por usuário - apenas para admin */}
          {isAdmin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontWeight: 600, fontSize: '15px', color: '#333', minWidth: 'max-content' }}>
                <i className='fas fa-filter me-2' style={{ color: 'var(--primary-color)' }}></i>
                Usuário:
              </label>
              <Select
                style={{ width: '250px', borderRadius: '4px' }}
                placeholder="Selecionar usuário..."
                allowClear
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                value={selectedUserId}
                onChange={handleUserChange}
                options={[
                  { label: 'Todos os usuários', value: null },
                  ...allUsers.map(u => ({
                    label: `${u.name} (${u.email})`,
                    value: u._id
                  }))
                ]}
                size="large"
                popupMatchSelectWidth={false}
              />
            </div>
          )}
        </div>
        {/* Tabs with Badges */}
        <Tabs 
          defaultActiveKey={filter} 
          activeKey={filter} 
          onChange={handleFilterChange}
          className='dashboard-tabs'
        >
          <Tabs.TabPane 
            tab={
              <span>
                <i className='fas fa-check-circle me-2'></i>
                Ativos
                <Badge count={activeCount} className='dashboard-badge' />
              </span>
            } 
            key='active' 
          />
          <Tabs.TabPane 
            tab={
              <span>
                <i className='fas fa-clock me-2'></i>
                Pendentes
                <Badge count={pendingCount} className='dashboard-badge' />
              </span>
            } 
            key='pending' 
          />
          <Tabs.TabPane 
            tab={
              <span>
                <i className='fas fa-times-circle me-2'></i>
                Vendidos
                <Badge count={closedCount} className='dashboard-badge' />
              </span>
            } 
            key='closed' 
          />
        </Tabs>

        {/* Products Grid */}
        <div className="user-dashboard-grid">
          {loading ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', paddingTop: '60px', paddingBottom: '60px' }}>
              <i className='fas fa-spinner fa-spin' style={{ fontSize: '48px', color: 'var(--primary-color)' }}></i>
              <p style={{ marginTop: '20px', fontSize: '16px', color: '#666' }}>Carregando produtos...</p>
            </div>
          ) : paginatedProducts.length === 0 ? (
            <Empty 
              description="Nenhum produto neste filtro" 
              style={{ gridColumn: '1 / -1', paddingTop: '60px', paddingBottom: '60px' }}
            />
          ) : (
            paginatedProducts.map((product, index) => renderProductCard(product, index))
          )}
        </div>

        {/* Pagination */}
        {!loading && currentProducts.length > countPerPage && (
          <Pagination
            pageSize={countPerPage}
            onChange={setCurrent}
            current={current}
            total={currentProducts.length}
            className='dashboard-pagination'
          />
        )}
      </div>
    </UnifiedDashboard>
  );
};

export default UserDashboard;
